import { useMemo, useState } from "react";
import type { Location, Settings } from "../lib/types";
import { CALC_METHODS } from "../lib/constants";
import { describeCalculationMethod, describeHijriSetting, getAutoHijriProfile } from "../lib/hijriRegional";
import { useLocale, calcMethodLabel } from "../lib/i18n";

export function SettingsDrawer(props: {
  value: Settings;
  location?: Location | null;
  onChange: (s: Settings) => void;
}) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const s = props.value;
  const autoProfile = useMemo(() => getAutoHijriProfile(props.location), [props.location]);
  const effectiveLabel = useMemo(() => describeHijriSetting(s, props.location), [props.location, s]);
  const methodLabel = useMemo(() => describeCalculationMethod(s, props.location), [props.location, s]);

  return (
    <>
      <button className="btn" onClick={() => setOpen(true)} aria-label={t.openSettingsAria}>
        {t.settings}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--overlay-bg)",
            zIndex: 200,
            display: "flex",
            justifyContent: "flex-end"
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            className="card"
            style={{
              width: "min(420px, 92vw)",
              height: "100%",
              borderRadius: 0,
              borderLeft: "1px solid var(--border)",
              padding: 16,
              overflow: "auto"
            }}
          >
            <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
              <div className="h2">{t.settings}</div>
              <button className="btn" onClick={() => setOpen(false)}>
                {t.close}
              </button>
            </div>

            <div className="p" style={{ marginBottom: 12 }}>
              {t.settingsIntro}
            </div>

            <div className="divider" />

            <div className="col" style={{ marginTop: 14 }}>
              <label className="p" style={{ fontSize: 12 }}>
                {t.calculationMethod}
              </label>
              <div className="row" style={{ flexWrap: "wrap" }}>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, calculationMethodMode: "auto" })}
                  style={{
                    background: s.calculationMethodMode !== "manual" ? "var(--accent-bg)" : undefined,
                    borderColor: s.calculationMethodMode !== "manual" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.autoByCity}
                </button>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, calculationMethodMode: "manual" })}
                  style={{
                    background: s.calculationMethodMode === "manual" ? "var(--accent-bg)" : undefined,
                    borderColor: s.calculationMethodMode === "manual" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.manual}
                </button>
              </div>
              <div className="p" style={{ fontSize: 12 }}>{methodLabel}</div>
              <select
                className="input"
                value={String(s.methodId)}
                onChange={(e) => props.onChange({ ...s, methodId: Number(e.target.value) })}
                disabled={s.calculationMethodMode !== "manual"}
              >
                {CALC_METHODS.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {calcMethodLabel(locale, m.id, m.label)}
                  </option>
                ))}
              </select>

              <label className="p" style={{ fontSize: 12, marginTop: 10 }}>
                {t.asrJuristic}
              </label>
              <div className="row">
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, madhhab: "shafi" })}
                  style={{
                    background: s.madhhab === "shafi" ? "var(--accent-bg)" : undefined,
                    borderColor: s.madhhab === "shafi" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.shafi}
                </button>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, madhhab: "hanafi" })}
                  style={{
                    background: s.madhhab === "hanafi" ? "var(--accent-bg)" : undefined,
                    borderColor: s.madhhab === "hanafi" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.hanafi}
                </button>
              </div>

              <label className="p" style={{ fontSize: 12, marginTop: 10 }}>
                {t.hijriDateMode}
              </label>
              <div className="row" style={{ flexWrap: "wrap" }}>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, hijriAdjustmentMode: "auto" })}
                  style={{
                    background: s.hijriAdjustmentMode === "auto" ? "var(--accent-bg)" : undefined,
                    borderColor: s.hijriAdjustmentMode === "auto" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.autoByRegion}
                </button>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, hijriAdjustmentMode: "manual" })}
                  style={{
                    background: s.hijriAdjustmentMode === "manual" ? "var(--accent-bg)" : undefined,
                    borderColor: s.hijriAdjustmentMode === "manual" ? "var(--accent-border)" : undefined
                  }}
                >
                  {t.manualOverride}
                </button>
              </div>

              <div className="card" style={{ marginTop: 10 }}>
                <div style={{ padding: 12 }} className="p">
                  <strong>{t.currentHijriProfile}:</strong> {effectiveLabel}
                  {s.hijriAdjustmentMode === "auto" ? (
                    <>
                      <br />
                      {t.regionDefault}: {autoProfile.adjustment > 0 ? `+${autoProfile.adjustment}` : String(autoProfile.adjustment)} {t.dayWord}.
                    </>
                  ) : null}
                </div>
              </div>

              <label className="p" style={{ fontSize: 12, marginTop: 10 }}>
                {t.manualHijriAdjustment}
              </label>
              <div className="row">
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, hijriAdjustmentMode: "manual", hijriAdjustment: Math.max(-2, s.hijriAdjustment - 1) })}
                  aria-label={t.decAdjAria}
                >
                  −
                </button>
                <div className="chip soft">
                  {s.hijriAdjustment > 0 ? `+${s.hijriAdjustment}` : String(s.hijriAdjustment)} {t.daysWord}
                </div>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, hijriAdjustmentMode: "manual", hijriAdjustment: Math.min(2, s.hijriAdjustment + 1) })}
                  aria-label={t.incAdjAria}
                >
                  +
                </button>
              </div>

              <label className="p" style={{ fontSize: 12, marginTop: 10 }}>
                {t.timeFormat}
              </label>
              <div className="row">
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, timeFormat: "24h" })}
                  style={{
                    background: s.timeFormat === "24h" ? "var(--accent-bg)" : undefined,
                    borderColor: s.timeFormat === "24h" ? "var(--accent-border)" : undefined
                  }}
                >
                  24h
                </button>
                <button
                  className="btn"
                  onClick={() => props.onChange({ ...s, timeFormat: "12h" })}
                  style={{
                    background: s.timeFormat === "12h" ? "var(--accent-bg)" : undefined,
                    borderColor: s.timeFormat === "12h" ? "var(--accent-border)" : undefined
                  }}
                >
                  12h
                </button>
              </div>

              <div style={{ marginTop: 16 }} className="card">
                <div style={{ padding: 12 }} className="p">
                  <strong>{t.noteLabel}:</strong> {t.settingsAutoNote}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
